import React, { useState } from 'react';
import { ApiService } from '../services/api';
import { ImageCompositor } from '../services/imageCompositor';

interface ProvisionerFormProps {
  apiService: ApiService;
  environment: string;
  onPreviewUpdate: (previewImageUrl: string, serialNumber: string, orientation: 'portrait' | 'landscape', canvas: HTMLCanvasElement | null, qrUrl?: string) => void;
  onLoadingChange: (isLoading: boolean) => void;
  hasPreview?: boolean;
}

export const ProvisionerForm: React.FC<ProvisionerFormProps> = ({ apiService, environment, onPreviewUpdate, onLoadingChange, hasPreview }) => {
  const [serialNumber, setSerialNumber] = useState('');
  const [url, setUrl] = useState('');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [imageCreated, setImageCreated] = useState(false);
  const [etchCompleted, setEtchCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentCanvas, setCurrentCanvas] = useState<HTMLCanvasElement | null>(null);
  const [showFormatWarning, setShowFormatWarning] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] = useState<{
    trimmedSerial: string;
    trimmedUrl: string;
    formatWarning: string;
  } | null>(null);
  const [showMappingConfirmation, setShowMappingConfirmation] = useState(false);
  const [pendingMappingData, setPendingMappingData] = useState<{
    trimmedSerial: string;
    trimmedUrl: string;
  } | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);

  // LDDL format characters - excludes I, L, O, Z for letters and 0, 1 for digits
  const ALLOWED_LETTERS = 'ABCDEFGHJKMNPQRSTUVW';
  const ALLOWED_DIGITS = '23456789';

  const validateSerialNumber = (value: string): { isValid: boolean; error?: string } => {
    // Trim whitespace first
    const trimmed = value.trim();
    
    if (!trimmed) {
      return { isValid: false, error: 'Serial number is required' };
    }

    // Normalize to uppercase
    const normalized = trimmed.toUpperCase();

    // Check if exactly 4 characters
    if (normalized.length !== 4) {
      return { isValid: false, error: 'Serial number must be exactly 4 characters long' };
    }

    return { isValid: true };
  };

  const checkSerialFormat = (value: string): { followsLDDL: boolean; warning?: string } => {
    // Trim whitespace and normalize to uppercase
    const normalized = value.trim().toUpperCase();
    
    if (normalized.length !== 4) {
      return { followsLDDL: false };
    }

    // Check LDDL format: Letter-Digit-Digit-Letter
    const [l1, d1, d2, l2] = normalized.split('');
    
    const issues: string[] = [];
    
    if (!ALLOWED_LETTERS.includes(l1)) {
      issues.push(`First character "${l1}" should be a letter from ${ALLOWED_LETTERS}`);
    }
    
    if (!ALLOWED_DIGITS.includes(d1)) {
      issues.push(`Second character "${d1}" should be a digit from ${ALLOWED_DIGITS}`);
    }
    
    if (!ALLOWED_DIGITS.includes(d2)) {
      issues.push(`Third character "${d2}" should be a digit from ${ALLOWED_DIGITS}`);
    }
    
    if (!ALLOWED_LETTERS.includes(l2)) {
      issues.push(`Fourth character "${l2}" should be a letter from ${ALLOWED_LETTERS}`);
    }

    if (issues.length > 0) {
      return { 
        followsLDDL: false, 
        warning: `Serial number "${normalized}" doesn't follow the expected LDDL format:\n${issues.join('\n')}` 
      };
    }

    return { followsLDDL: true };
  };

  const validateUrl = (url: string, environment: string): { isValid: boolean; error?: string } => {
    // Trim whitespace first
    const trimmed = url.trim();
    
    if (!trimmed) {
      return { isValid: false, error: 'URL is required' };
    }

    // Environment-specific base URLs
    const environmentUrls: Record<string, string> = {
      'production': 'https://qr.butterflymx.com/i/',
      'staging': 'https://viceroy-service-staging.platform-services-dev.butterflymx.com/i/',
      'development': 'https://viceroy-service-dev.platform-services-dev.butterflymx.com/i/'
    };

    const expectedBase = environmentUrls[environment];
    if (!expectedBase) {
      return { isValid: false, error: `Unknown environment: ${environment}` };
    }

    if (!trimmed.startsWith(expectedBase)) {
      return { isValid: false, error: `URL must start with ${expectedBase} for ${environment} environment` };
    }

    // Check that there's a QR identifier after the base
    const qrIdentifier = trimmed.substring(expectedBase.length);
    if (!qrIdentifier || qrIdentifier.trim().length === 0) {
      return { isValid: false, error: 'URL must contain a QR identifier after /i/' };
    }

    if (/\s/.test(qrIdentifier)) {
      return { isValid: false, error: 'QR identifier cannot contain whitespace' };
    }

    return { isValid: true };
  };

  const handleAutoGenerate = async (selectedOrientation: 'portrait' | 'landscape') => {
    setError(null);
    setSuccess(null);
    setGenerating(true);
    onLoadingChange(true);

    try {
      // 1. Generate serial number and UUID
      const [serialResponse, uuidResponse] = await Promise.all([
        apiService.generateSerial(environment),
        apiService.generateUuid(environment)
      ]);

      // 2. Fetch template
      const templateResponse = await apiService.getTemplate(selectedOrientation, environment);

      // 3. Generate composite image
      const compositeResult = selectedOrientation === 'portrait' 
        ? await ImageCompositor.generatePortraitComposite(
            templateResponse.data,
            uuidResponse.url,
            serialResponse.serialNumber
          )
        : await ImageCompositor.generateLandscapeComposite(
            templateResponse.data,
            uuidResponse.url,
            serialResponse.serialNumber
          );

      // 4. Update UI with generated data and preview
      setSerialNumber(serialResponse.serialNumber);
      setUrl(uuidResponse.url);
      setOrientation(selectedOrientation);
      setImageCreated(true); // Auto-set since we generated the image
      setEtchCompleted(false);
      setCurrentCanvas(compositeResult.canvas);
      onPreviewUpdate(compositeResult.dataUrl, serialResponse.serialNumber, selectedOrientation, compositeResult.canvas, uuidResponse.url);

      setSuccess(`Successfully generated ${selectedOrientation} QR code for serial ${serialResponse.serialNumber}.`);

    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to generate ${selectedOrientation} QR code`);
    } finally {
      setGenerating(false);
      onLoadingChange(false);
    }
  };

  const hasAnyData = () => {
    return serialNumber !== '' || url !== '';
  };

  const handleClear = () => {
    setSerialNumber('');
    setUrl('');
    setOrientation('portrait');
    setImageCreated(false);
    setEtchCompleted(false);
    setCurrentCanvas(null);
    setError(null);
    setSuccess(null);
    onPreviewUpdate('', '', 'portrait', null);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Trim inputs before validation
    const trimmedSerial = serialNumber.trim();
    const trimmedUrl = url.trim();

    // Validate serial number (length only)
    const serialValidation = validateSerialNumber(trimmedSerial);
    if (!serialValidation.isValid) {
      setError(serialValidation.error!);
      return;
    }

    // Validate URL
    const urlValidation = validateUrl(trimmedUrl, environment);
    if (!urlValidation.isValid) {
      setError(urlValidation.error!);
      return;
    }

    // Check if serial follows expected LDDL format
    const formatCheck = checkSerialFormat(trimmedSerial);
    if (!formatCheck.followsLDDL && formatCheck.warning) {
      // Show warning dialog and store data for potential submission
      setPendingSubmissionData({
        trimmedSerial,
        trimmedUrl,
        formatWarning: formatCheck.warning
      });
      setShowFormatWarning(true);
      return;
    }

    // If there's a preview, show confirmation dialog
    if (hasPreview && currentCanvas) {
      setPendingMappingData({
        trimmedSerial,
        trimmedUrl
      });
      setShowMappingConfirmation(true);
      return;
    }

    // If format is good and no preview or user confirmed, proceed with submission
    await performSubmission(trimmedSerial, trimmedUrl);
  };

  const performSubmission = async (trimmedSerial: string, trimmedUrl: string) => {
    setLoading(true);
    try {
      const result = await apiService.createMapping(
        trimmedSerial, 
        trimmedUrl, 
        orientation,
        imageCreated,
        etchCompleted,
        environment
      );
      const createdByText = result.createdBy ? ` (created by ${result.createdBy})` : '';
      setSuccess(`Successfully created mapping: ${result.serialNumber} -> ${result.qrIdentifier}${createdByText}`);
      setSerialNumber('');
      setUrl('');
      setOrientation('portrait');
      setImageCreated(false);
      setEtchCompleted(false);
    } catch (err: any) {
      if (err.response?.status === 409) {
        // Display the specific error message from the backend
        const message = err.response?.data?.message;
        if (message === 'Serial number already exists') {
          setError('Serial number already exists');
        } else if (message === 'QR identifier already exists') {
          setError('QR identifier already exists');
        } else {
          setError(message || 'Duplicate entry detected');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to create mapping');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSubmission = () => {
    if (pendingSubmissionData) {
      setShowFormatWarning(false);
      setPendingSubmissionData(null);
      performSubmission(pendingSubmissionData.trimmedSerial, pendingSubmissionData.trimmedUrl);
    }
  };

  const handleCancelSubmission = () => {
    setShowFormatWarning(false);
    setPendingSubmissionData(null);
  };

  const handleConfirmMapping = () => {
    if (pendingMappingData) {
      setShowMappingConfirmation(false);
      setPendingMappingData(null);
      performSubmission(pendingMappingData.trimmedSerial, pendingMappingData.trimmedUrl);
    }
  };

  const handleCancelMapping = () => {
    setShowMappingConfirmation(false);
    setPendingMappingData(null);
  };

  return (
    <div style={containerStyle}>
      <div className="provisioner-form">
          <h2 style={titleStyle}>🔗 QR Code Generator</h2>
          
          {/* Auto-generate buttons */}
          <div style={buttonGroupStyle}>
            <button 
              type="button"
              onClick={() => handleAutoGenerate('portrait')}
              disabled={loading || generating}
              style={{
                ...generateButtonStyle,
                opacity: (loading || generating) ? 0.6 : 1,
                minWidth: '150px', // Fixed width to prevent shrinking
              }}
            >
              {generating ? 'Generating...' : '📱 Create Portrait'}
            </button>
            <button 
              type="button"
              onClick={() => handleAutoGenerate('landscape')}
              disabled={loading || generating}
              style={{
                ...generateButtonStyle,
                opacity: (loading || generating) ? 0.6 : 1,
                minWidth: '150px', // Fixed width to prevent shrinking
              }}
            >
              {generating ? 'Generating...' : '🖥️ Create Landscape'}
            </button>
          </div>

          {hasAnyData() && (
            <div style={clearSectionStyle}>
              <button 
                type="button"
                onClick={handleClear}
                style={clearButtonStyle}
              >
                🗑️ Clear & Start Over
              </button>
            </div>
          )}

          {/* Manual Entry Toggle - only show if no preview */}
          {!hasPreview && (
            <div style={manualEntryToggleStyle}>
              <button
                type="button"
                onClick={() => setShowManualEntry(!showManualEntry)}
                style={toggleButtonStyle}
              >
                {showManualEntry ? '🔼' : '🔽'} Manual Entry (Import Existing QR Codes Only)
              </button>
            </div>
          )}

          {/* Manual form - only show if no preview and manual entry is toggled */}
          {!hasPreview && showManualEntry && (
          <div style={expandedFormStyle}>
            <div style={warningBoxStyle}>
              <span style={warningIconStyle}>⚠️</span>
              <p style={warningTextStyle}>
                <strong>Important:</strong> Manual entry should only be used when importing existing QR codes.
                For new QR codes, use the "Create Portrait" or "Create Landscape" buttons above for automatic generation.
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>🏷️ Serial Number:</label>
                <input
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder="e.g., A22B"
                  disabled={loading || generating}
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>🌐 URL:</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://qr.butterflymx.com/i/..."
                  disabled={loading || generating}
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>📐 Orientation:</label>
                <select
                  value={orientation}
                  onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
                  disabled={loading || generating}
                  style={selectStyle}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
              <div style={checkboxGroupStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={imageCreated}
                    onChange={(e) => setImageCreated(e.target.checked)}
                    disabled={loading || generating}
                    style={checkboxStyle}
                  />
                  🖼️ Image Created
                </label>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={etchCompleted}
                    onChange={(e) => setEtchCompleted(e.target.checked)}
                    disabled={loading || generating}
                    style={checkboxStyle}
                  />
                  ✅ Etch Completed
                </label>
              </div>
              <button
                type="submit"
                disabled={loading || generating}
                style={{
                  ...submitButtonStyle,
                  opacity: (loading || generating) ? 0.6 : 1,
                }}
              >
                {loading ? 'Creating...' : 'Create Mapping'}
              </button>
            </form>
          </div>
          )}

          {/* Message when preview is shown */}
          {hasPreview && (
            <div style={previewInstructionsStyle}>
              <h3 style={sectionTitleStyle}>🎉 QR Code Ready!</h3>
              <p style={instructionTextStyle}>
                Your QR code has been generated. Click the <strong>"Generate & Upload"</strong> button in the preview panel to:
              </p>
              <ol style={instructionListStyle}>
                <li>Generate a high-resolution TIFF file</li>
                <li>Upload to Google Drive</li>
                <li>Automatically create the device mapping</li>
              </ol>
              <div style={tipBoxStyle}>
                <span style={tipIconStyle}>💡</span>
                <p style={tipTextStyle}>
                  <strong>Tip:</strong> You can also download a PNG preview for quick reference.
                </p>
              </div>
            </div>
          )}

          {/* Error and success messages */}
          {error && (
            <div style={errorStyle}>
              ❌ {error}
            </div>
          )}
          {success && (
            <div style={successStyle}>
              ✅ {success}
            </div>
          )}

          {/* Format warning dialog */}
          {showFormatWarning && pendingSubmissionData && (
            <div style={overlayStyle}>
              <div style={dialogStyle}>
                <div style={dialogHeaderStyle}>
                  <h3 style={dialogTitleStyle}>⚠️ Serial Number Format Warning</h3>
                </div>
                <div style={dialogBodyStyle}>
                  <p style={dialogTextStyle}>
                    {pendingSubmissionData.formatWarning}
                  </p>
                  <p style={dialogSubtextStyle}>
                    The expected format is <strong>Letter-Digit-Digit-Letter</strong> (e.g., A23B) using:
                  </p>
                  <ul style={dialogListStyle}>
                    <li>Letters: {ALLOWED_LETTERS}</li>
                    <li>Digits: {ALLOWED_DIGITS}</li>
                  </ul>
                  <p style={dialogQuestionStyle}>
                    Do you want to proceed with this serial number anyway?
                  </p>
                </div>
                <div style={dialogFooterStyle}>
                  <button 
                    onClick={handleCancelSubmission}
                    style={dialogCancelButtonStyle}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleConfirmSubmission}
                    style={dialogConfirmButtonStyle}
                  >
                    Proceed Anyway
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mapping confirmation dialog */}
          {showMappingConfirmation && pendingMappingData && (
            <div style={overlayStyle}>
              <div style={dialogStyle}>
                <div style={dialogHeaderStyle}>
                  <h3 style={dialogTitleStyle}>🔗 Create Device Mapping</h3>
                </div>
                <div style={dialogBodyStyle}>
                  <p style={dialogSubtextStyle}>
                    You're about to create a device mapping for <strong>{pendingMappingData.trimmedSerial}</strong> with a preview image shown.
                  </p>
                  
                  <div style={checklistStyle}>
                    <div style={checkItemStyle}>
                      <span style={checkboxStyleDialog}>✓</span>
                      <span>The preview shows the correct serial number and orientation</span>
                    </div>
                    <div style={checkItemStyle}>
                      <span style={checkboxStyleDialog}>✓</span>
                      <span>You have downloaded or will download the files needed</span>
                    </div>
                    <div style={checkItemStyle}>
                      <span style={checkboxStyleDialog}>✓</span>
                      <span>You're ready to create the device mapping in the database</span>
                    </div>
                  </div>
                  
                  <p style={dialogQuestionStyle}>
                    Do you want to create the device mapping now?
                  </p>
                </div>
                <div style={dialogFooterStyle}>
                  <button 
                    onClick={handleCancelMapping}
                    style={dialogCancelButtonStyle}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleConfirmMapping}
                    style={mappingConfirmButtonStyle}
                  >
                    Create Mapping
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  padding: '12px',
  background: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  overflowY: 'auto',
};

const titleStyle: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '20px',
  fontWeight: '600',
  color: '#6600ff',
  textAlign: 'center',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  marginBottom: '12px',
};

const generateButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #6600ff, #4d00c0)',
  color: 'white',
  border: 'none',
  padding: '12px 16px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s',
  boxShadow: '0 3px 8px rgba(102, 0, 255, 0.3)',
  flex: '1',
};

const clearSectionStyle: React.CSSProperties = {
  marginBottom: '10px',
  paddingBottom: '10px',
  borderBottom: '2px solid #e0e0e0',
};

const clearButtonStyle: React.CSSProperties = {
  background: '#dc3545',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  width: '100%',
};

const expandedFormStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '2px solid #6600ff',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0 2px 8px rgba(102, 0, 255, 0.1)',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '17px',
  fontWeight: '500',
  color: '#333',
};

const formGroupStyle: React.CSSProperties = {
  marginBottom: '10px',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: '500',
  color: '#333',
  fontSize: '14px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '2px solid #e0e0e0',
  borderRadius: '6px',
  fontSize: '14px',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '2px solid #e0e0e0',
  borderRadius: '6px',
  fontSize: '14px',
  background: 'white',
  boxSizing: 'border-box',
};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '15px',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  color: '#333',
  cursor: 'pointer',
};

const checkboxStyle: React.CSSProperties = {
  margin: '0',
  cursor: 'pointer',
};

const submitButtonStyle: React.CSSProperties = {
  width: '100%',
  background: '#28a745',
  color: 'white',
  border: 'none',
  padding: '12px 20px',
  borderRadius: '6px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const errorStyle: React.CSSProperties = {
  background: '#f8d7da',
  border: '1px solid #f5c6cb',
  borderRadius: '6px',
  padding: '12px',
  marginTop: '15px',
  color: '#721c24',
  fontSize: '14px',
};

const successStyle: React.CSSProperties = {
  background: '#d4edda',
  border: '1px solid #c3e6cb',
  borderRadius: '6px',
  padding: '12px',
  marginTop: '15px',
  color: '#155724',
  fontSize: '14px',
};

// Warning dialog styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  maxWidth: '500px',
  width: '90%',
  maxHeight: '80vh',
  overflow: 'hidden',
};

const dialogHeaderStyle: React.CSSProperties = {
  padding: '20px 24px 0 24px',
  borderBottom: '1px solid #eee',
  paddingBottom: '16px',
};

const dialogTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const dialogBodyStyle: React.CSSProperties = {
  padding: '20px 24px',
  fontSize: '14px',
  lineHeight: '1.5',
};

const dialogTextStyle: React.CSSProperties = {
  margin: '0 0 16px 0',
  color: '#666',
  whiteSpace: 'pre-line',
  fontFamily: 'monospace',
  backgroundColor: '#f8f9fa',
  padding: '12px',
  borderRadius: '6px',
  border: '1px solid #e9ecef',
};

const dialogSubtextStyle: React.CSSProperties = {
  margin: '16px 0 8px 0',
  color: '#333',
};

const dialogListStyle: React.CSSProperties = {
  margin: '8px 0 16px 20px',
  color: '#666',
};

const dialogQuestionStyle: React.CSSProperties = {
  margin: '16px 0 0 0',
  color: '#333',
  fontWeight: '500',
};

const dialogFooterStyle: React.CSSProperties = {
  padding: '0 24px 20px 24px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
};

const dialogCancelButtonStyle: React.CSSProperties = {
  background: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const dialogConfirmButtonStyle: React.CSSProperties = {
  background: '#ffc107',
  color: '#212529',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const checklistStyle: React.CSSProperties = {
  margin: '16px 0',
  padding: '16px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  border: '1px solid #e9ecef',
};

const checkItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '8px 0',
  fontSize: '14px',
  color: '#555',
};

const checkboxStyleDialog: React.CSSProperties = {
  color: '#28a745',
  fontWeight: 'bold',
  fontSize: '16px',
};

const mappingConfirmButtonStyle: React.CSSProperties = {
  background: '#6600ff',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const warningBoxStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  padding: '12px',
  backgroundColor: '#fff3cd',
  border: '1px solid #ffc107',
  borderRadius: '6px',
  marginBottom: '16px',
};

const warningIconStyle: React.CSSProperties = {
  fontSize: '20px',
  flexShrink: 0,
};

const warningTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '13px',
  color: '#856404',
  lineHeight: '1.4',
};

const previewInstructionsStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '2px solid #6600ff',
  borderRadius: '8px',
  padding: '20px',
  textAlign: 'center',
};

const instructionTextStyle: React.CSSProperties = {
  fontSize: '15px',
  color: '#444',
  margin: '12px 0',
  lineHeight: '1.5',
};

const instructionListStyle: React.CSSProperties = {
  textAlign: 'left',
  maxWidth: '400px',
  margin: '16px auto',
  fontSize: '14px',
  color: '#555',
  lineHeight: '1.8',
};

const tipBoxStyle: React.CSSProperties = {
  display: 'flex',
  gap: '10px',
  padding: '12px',
  backgroundColor: '#e8f5e8',
  border: '1px solid #c3e6c3',
  borderRadius: '6px',
  marginTop: '16px',
  alignItems: 'center',
};

const tipIconStyle: React.CSSProperties = {
  fontSize: '20px',
  flexShrink: 0,
};

const tipTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '13px',
  color: '#155724',
  lineHeight: '1.4',
  textAlign: 'left',
};

const manualEntryToggleStyle: React.CSSProperties = {
  marginBottom: '10px',
  textAlign: 'center',
};

const toggleButtonStyle: React.CSSProperties = {
  background: '#f8f9fa',
  border: '2px solid #dee2e6',
  borderRadius: '6px',
  padding: '10px 16px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  color: '#6c757d',
  transition: 'all 0.2s',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};